import React from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '../../../atoms'
import {
  ToolbarContainer,
  ToolbarSection,
  ToolbarButton,
  ToolbarInput,
  PageInfo,
  ZoomInfo,
} from './PDFToolbar.styles'

export interface PDFToolbarProps {
  pageNumber: number
  numPages: number
  scale: number
  rotation: number
  onPageChange: (page: number) => void
  onZoomIn: () => void
  onZoomOut: () => void
  onResetZoom: () => void
  onRotateLeft: () => void
  enableDownload?: boolean
  enableSearch?: boolean
  onToggleSearch?: () => void
  pdfUrl?: string
}

export const PDFToolbar: React.FC<PDFToolbarProps> = ({
  pageNumber,
  numPages,
  scale,
  rotation,
  onPageChange,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  onRotateLeft,
  enableDownload = true,
  enableSearch = true,
  onToggleSearch,
  pdfUrl,
}) => {
  const { t } = useTranslation('ui')
  const [inputPage, setInputPage] = React.useState(pageNumber.toString())

  // Update input when page changes externally
  React.useEffect(() => {
    setInputPage(pageNumber.toString())
  }, [pageNumber])

  const handlePreviousPage = () => {
    if (pageNumber > 1) {
      onPageChange(pageNumber - 1)
    }
  }

  const handleNextPage = () => {
    if (pageNumber < numPages) {
      onPageChange(pageNumber + 1)
    }
  }

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputPage(e.target.value)
  }

  const handlePageInputBlur = () => {
    const page = parseInt(inputPage, 10)
    if (!isNaN(page) && page >= 1 && page <= numPages) {
      onPageChange(page)
    } else {
      setInputPage(pageNumber.toString())
    }
  }

  const handlePageInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handlePageInputBlur()
    }
  }

  const handleDownload = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank')
    }
  }

  const zoomPercentage = Math.round(scale * 100)

  return (
    <ToolbarContainer>
      {/* Page Navigation */}
      <ToolbarSection>
        <ToolbarButton
          onClick={handlePreviousPage}
          disabled={pageNumber <= 1}
          title={t('pdfViewer.toolbar.previousPage')}
        >
          <Icon name="chevron-left" size="small" />
        </ToolbarButton>

        <PageInfo>
          <ToolbarInput
            type="text"
            value={inputPage}
            onChange={handlePageInputChange}
            onBlur={handlePageInputBlur}
            onKeyDown={handlePageInputKeyDown}
          />
          <span>
            {t('pdfViewer.toolbar.of')} {numPages}
          </span>
        </PageInfo>

        <ToolbarButton
          onClick={handleNextPage}
          disabled={pageNumber >= numPages}
          title={t('pdfViewer.toolbar.nextPage')}
        >
          <Icon name="chevron-right" size="small" />
        </ToolbarButton>
      </ToolbarSection>

      {/* Zoom Controls */}
      <ToolbarSection>
        <ToolbarButton
          onClick={onZoomOut}
          disabled={scale <= 0.2}
          title={t('pdfViewer.toolbar.zoomOut')}
        >
          <Icon name="zoom-out" size="small" />
        </ToolbarButton>

        <ZoomInfo
          onClick={onResetZoom}
          title={t('pdfViewer.toolbar.resetZoom')}
        >
          {zoomPercentage}%
        </ZoomInfo>

        <ToolbarButton
          onClick={onZoomIn}
          disabled={scale >= 3.0}
          title={t('pdfViewer.toolbar.zoomIn')}
        >
          <Icon name="zoom-in" size="small" />
        </ToolbarButton>

        <ToolbarButton
          onClick={onRotateLeft}
          title={t('pdfViewer.toolbar.rotateLeft')}
        >
          <Icon name="rotate-ccw-square" size="small" />
        </ToolbarButton>
      </ToolbarSection>

      {/* Document Actions */}
      <ToolbarSection>
        {enableSearch && onToggleSearch && (
          <ToolbarButton
            onClick={onToggleSearch}
            title={t('pdfViewer.toolbar.searchShortcut')}
          >
            <Icon name="search" size="small" />
          </ToolbarButton>
        )}
        {enableDownload && (
          <ToolbarButton
            onClick={handleDownload}
            title={t('pdfViewer.toolbar.downloadPdf')}
          >
            <Icon name="download" size="small" />
          </ToolbarButton>
        )}
      </ToolbarSection>
    </ToolbarContainer>
  )
}
